/**
 * Referral Card Component
 * VOY-133: Friend referral and rewards system
 */

import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Users, Copy, Share2, Gift, Check } from 'lucide-react';

interface ReferralMilestone {
  friends: number;
  reward: string;
  unlocked: boolean;
}

interface ReferralCardProps {
  referralCode: string;
  friendsJoined: number;
  milestones: ReferralMilestone[];
  onCopyLink: () => void;
  onShare: () => void;
}

export function ReferralCard({ 
  referralCode, 
  friendsJoined, 
  milestones, 
  onCopyLink, 
  onShare 
}: ReferralCardProps) {
  return (
    <Card className="rounded-3xl bg-white/[0.08] dark:bg-zinc-800/50 backdrop-blur-xl border border-white/30 dark:border-zinc-700/50 shadow-[0_8px_24px_rgba(0,0,0,0.15)] dark:shadow-xl">
      <CardContent className="p-5">
        {/* Header */}
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
            <Users className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-900 dark:text-white">Invite Friends, Earn Rewards</h3>
            <p className="text-sm text-gray-600 dark:text-zinc-400">Both of you get bonuses!</p>
          </div>
        </div>

        {/* Referral Code */}
        <div className="bg-white dark:bg-zinc-700/50 rounded-lg p-3 mb-4 border border-purple-200 dark:border-zinc-600">
          <p className="text-xs text-gray-500 dark:text-zinc-400 mb-1">Your referral code:</p>
          <div className="flex items-center justify-between">
            <code className="text-lg font-bold text-purple-600 dark:text-purple-400">{referralCode}</code>
            <Button
              onClick={onCopyLink}
              size="sm"
              variant="outline"
              className="rounded-full"
            >
              <Copy className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Friends Count */}
        <div className="bg-purple-100 dark:bg-zinc-700/50 rounded-lg p-3 mb-4">
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold text-purple-900 dark:text-white">Friends Joined</span>
            <span className="text-2xl font-bold text-purple-600 dark:text-purple-400">{friendsJoined}</span>
          </div>
        </div>

        {/* Milestones */}
        <div className="mb-4">
          <p className="text-sm font-semibold text-gray-700 dark:text-white mb-2">Rewards Unlocked:</p>
          <div className="space-y-2">
            {milestones.map((milestone) => (
              <div
                key={milestone.friends}
                className={`flex items-center justify-between p-2 rounded-lg ${
                  milestone.unlocked ? 'bg-green-100 dark:bg-green-900/30' : 'bg-gray-100 dark:bg-zinc-700/50'
                }`}
              >
                <div className="flex items-center gap-2">
                  {milestone.unlocked ? (
                    <Check className="w-4 h-4 text-green-600 dark:text-green-400" />
                  ) : (
                    <Gift className="w-4 h-4 text-gray-400 dark:text-zinc-500" />
                  )}
                  <span className={`text-sm ${
                    milestone.unlocked ? 'text-green-800 dark:text-green-400 font-semibold' : 'text-gray-600 dark:text-zinc-400'
                  }`}>
                    {milestone.friends} friends
                  </span>
                </div>
                <span className={`text-xs ${
                  milestone.unlocked ? 'text-green-700 dark:text-green-400' : 'text-gray-500 dark:text-zinc-500'
                }`}>
                  {milestone.reward}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Share Button */}
        <Button
          onClick={onShare}
          className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white font-semibold rounded-full"
        >
          <Share2 className="w-4 h-4 mr-2" />
          Share on Social Media
        </Button>
      </CardContent>
    </Card>
  );
}

